import type { ISortElement } from 'src/shared/types/filters';

import { useState } from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import {
  Box,
  Menu,
  List,
  Button,
  darken,
  styled,
  MenuItem,
  ListItem,
  Typography,
  ListItemText,
  ListItemButton,
} from '@mui/material';

import { Drawer } from 'src/shared/components/drawer';
import { APP_MENU_PROPS, APP_MENU_ITEM_SX } from 'src/shared/components/menu/menu-styles';

const SortButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  paddingInline: 10,
  paddingBlock: 6,
  height: 32,
  fontSize: 13,
  borderRadius: 999,
  border: 'none',
  boxShadow: 'none',
  minWidth: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,

  '&:hover': {
    boxShadow: 'none',
    backgroundColor: darken(theme.palette.background.paper, 0.04),
  },
  '&.Mui-focusVisible': {
    boxShadow: 'none',
  },
  [theme.breakpoints.down('md')]: {
    minHeight: 44,
    height: 44,
    paddingBlock: 10,
  },
}));

interface SortMenuProps {
  items: ISortElement[];
  onChangeSort: (sort: ISortElement) => void;
  sort: ISortElement;
}

export const SortMenu = ({ items, sort, onChangeSort }: SortMenuProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [selected, setSelected] = useState(sort);
  const [desktopAnchor, setDesktopAnchor] = useState<HTMLElement | null>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const isDesktopOpen = Boolean(desktopAnchor);
  const open = isDesktop ? isDesktopOpen : isMobileOpen;

  const onCloseMenu = () => {
    setDesktopAnchor(null);
    setIsMobileOpen(false);
  };

  const onOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDesktop) {
      if (desktopAnchor) {
        onCloseMenu();
      } else {
        setDesktopAnchor(e.currentTarget);
      }
      return;
    }

    if (isMobileOpen) {
      onCloseMenu();
    } else {
      setIsMobileOpen(true);
    }
  };

  const onSelectItem = (item: ISortElement) => {
    setSelected(item);
    onChangeSort(item);
    onCloseMenu();
  };

  return (
    <Box>
      <SortButton
        sx={(sxTheme) => ({
          backgroundColor: open
            ? sxTheme.palette.action.selected
            : sxTheme.palette.background.paper,
        })}
        variant="outlined"
        onClick={onOpenMenu}
        startIcon={<SwapVertRoundedIcon />}
      >
        <Typography component="span" fontWeight={600} fontSize={13}>
          {selected.title}
        </Typography>
      </SortButton>
      {isDesktop ? (
        <Menu
          open={open}
          anchorEl={desktopAnchor}
          onClose={onCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          {...APP_MENU_PROPS}
        >
          {items.map((item) => (
            <MenuItem
              key={`${item.value}-${item.order}`}
              selected={item.value === selected.value && item.order === selected.order}
              sx={{
                ...APP_MENU_ITEM_SX,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  color: 'text.primary',
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'action.selected',
                },
                '&.Mui-focusVisible': {
                  bgcolor: 'action.hover',
                },
                justifyContent: 'flex-start',
                textAlign: 'left',
                color: 'text.primary',
                fontWeight:
                  item.value === selected.value && item.order === selected.order ? 700 : 400,
              }}
              onClick={() => onSelectItem(item)}
            >
              <Typography variant="body2" sx={{ fontSize: 14 }}>
                {item.title}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      ) : (
        <Drawer open={open} onClose={onCloseMenu} title="Сортировка">
          <List disablePadding sx={{ pb: 0.5 }}>
            {items.map((item) => (
              <ListItem disablePadding key={`${item.value}-${item.order}`}>
                <ListItemButton
                  selected={item.value === selected.value && item.order === selected.order}
                  onClick={() => onSelectItem(item)}
                >
                  <ListItemText
                    primary={item.title}
                    slotProps={{
                      primary: {
                        fontWeight:
                          item.value === selected.value && item.order === selected.order
                            ? 700
                            : 400,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      )}
    </Box>
  );
};
